import {
  CreateTaskDto,
  IPolicyStep,
  ITask,
  ITaskCountsResult,
  ReorderTasksDto,
  TaskFilterDto,
  TaskPriority,
  TaskStatus,
  UpdateTaskDto,
} from 'admiral-core';
import { AxiosResponse } from 'axios';
import { AdmiralApi, BASE_URL } from '@/services/AdmiralApi';

export class TaskApi {
  static getTasks(filter?: TaskFilterDto): Promise<AxiosResponse<ITask[]>> {
    return AdmiralApi.apiCall<ITask[]>({
      method: 'POST',
      url: `${BASE_URL}/task/list`,
      data: filter,
    });
  }

  static getTaskDetail(taskId: string): Promise<AxiosResponse<ITask>> {
    return AdmiralApi.apiCall<ITask>({
      method: 'POST',
      url: `${BASE_URL}/task/detail`,
      data: { taskId },
    });
  }

  static getTaskCounts(filter?: TaskFilterDto): Promise<AxiosResponse<ITaskCountsResult>> {
    return AdmiralApi.apiCall<ITaskCountsResult>({
      method: 'POST',
      url: `${BASE_URL}/task/list/count`,
      data: filter,
    });
  }

  static reorder(dto: ReorderTasksDto): Promise<AxiosResponse<void>> {
    return AdmiralApi.apiCall<void>({
      method: 'POST',
      url: `${BASE_URL}/task/reorder`,
      data: dto,
    });
  }

  static update(id: string, updateTaskDto: UpdateTaskDto): Promise<AxiosResponse<ITask>> {
    const formData = new FormData();
    formData.append('body', JSON.stringify(updateTaskDto));
    updateTaskDto.newUploadedFiles.forEach((file) => formData.append('files', file));

    return AdmiralApi.apiCall<ITask>({
      method: 'PATCH',
      url: `${BASE_URL}/task/${id}`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
  static create(createTaskDto: CreateTaskDto): Promise<AxiosResponse<ITask>> {
    const formData = new FormData();
    formData.append('body', JSON.stringify(createTaskDto));
    createTaskDto.newUploadedFiles.forEach((file) => formData.append('files', file));
    return AdmiralApi.apiCall<ITask>({
      method: 'POST',
      url: `${BASE_URL}/task/create`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static delete(id: string): Promise<AxiosResponse<void>> {
    return AdmiralApi.apiCall<void>({
      method: 'DELETE',
      url: `${BASE_URL}/task/${id}`,
    });
  }

  static getSteps(taskId: string): Promise<AxiosResponse<IPolicyStep[]>> {
    return AdmiralApi.apiCall<IPolicyStep[]>({
      url: `${BASE_URL}/task/get/steps`,
      method: 'POST',
      data: { taskId },
    });
  }

  static updateStatus(
    id: string,
    task: ITask,
    newStatus: TaskStatus,
  ): Promise<AxiosResponse<ITask>> {
    return this.update(id, {
      agency: task.agency,
      title: task.title,
      description: task.description,
      important: task.important,
      priority: task.priority,
      dueAt: task.dueAt,
      tags: task.tags || [],
      users: task.users || [],
      status: newStatus,
      files: task.files || [],
      newUploadedFiles: [],
      isPrivate: task.isPrivate,
    });
  }

  static updatePriority(
    id: string,
    task: ITask,
    newPriority: TaskPriority,
  ): Promise<AxiosResponse<ITask>> {
    return this.update(id, {
      agency: task.agency,
      title: task.title,
      description: task.description,
      important: task.important,
      priority: newPriority,
      dueAt: task.dueAt,
      tags: task.tags || [],
      users: task.users || [],
      status: task.status,
      files: task.files || [],
      newUploadedFiles: [],
      isPrivate: task.isPrivate,
    });
  }

  static updateDueDate(id: string, task: ITask, newDueAt: Date): Promise<AxiosResponse<ITask>> {
    return this.update(id, {
      agency: task.agency,
      title: task.title,
      description: task.description,
      important: task.important,
      priority: task.priority,
      dueAt: newDueAt,
      tags: task.tags || [],
      users: task.users || [],
      status: task.status,
      files: task.files || [],
      newUploadedFiles: [],
      isPrivate: task.isPrivate,
    });
  }
}
